import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Filter, X, Search, RotateCcw, Tag } from 'lucide-react';
import {
  LEAD_STAGE_OPTIONS, INDIAN_STATES, LEAD_SOURCE_OPTIONS,
  PROPERTY_STATUS_OPTIONS, EXISTING_OTA_OPTIONS, WEBSITE_AVAILABLE_OPTIONS,
  INTERESTED_PLAN_OPTIONS, SETUP_PAYMENT_STATUS_OPTIONS, CHECKLIST_STATUS_OPTIONS,
  OTA_NAME_OPTIONS, OTA_STATUS_OPTIONS
} from '../constants/dropdownOptions';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const FILTER_SECTIONS = [
  {
    id: 'lead_info', label: 'Lead Information',
    filters: [
      { key: 'lead_stage', label: 'Lead Stage', type: 'dropdown', options: LEAD_STAGE_OPTIONS.filter(o => o.value) },
      { key: 'lead_owner', label: 'Lead Owner', type: 'search', op: 'regex' },
      { key: 'source', label: 'Source', type: 'dropdown', options: LEAD_SOURCE_OPTIONS.filter(o => o.value && o.value !== '__other__') },
      { key: 'property_status', label: 'Property Status', type: 'dropdown', options: PROPERTY_STATUS_OPTIONS.filter(o => o.value) },
      { key: 'verification', label: 'Verification', type: 'dropdown', options: [
        { value: 'verified', label: 'Verified' }, { value: 'unverified', label: 'Unverified' }
      ]},
      { key: 'onboarding_status', label: 'Onboarding Status', type: 'dropdown', options: [
        { value: 'Not Started', label: 'Not Started' }, { value: 'In Progress', label: 'In Progress' }, { value: 'Completed', label: 'Completed' }
      ]},
      { key: 'lead_date', label: 'Lead Date', type: 'date_range' },
      { key: 'next_follow_up_date', label: 'Follow-up Date', type: 'date_range' },
      { key: 'created_at', label: 'Created Date', type: 'date_range' },
    ],
  },
  {
    id: 'address', label: 'Address Information',
    filters: [
      { key: 'state', label: 'State', type: 'dropdown', options: INDIAN_STATES.filter(o => o.value) },
      { key: 'city', label: 'City', type: 'search', op: 'regex' },
      { key: 'pincode', label: 'Pincode', type: 'search', op: 'regex' },
      { key: 'country', label: 'Country', type: 'search', op: 'regex' },
      { key: 'website_available', label: 'Website Available', type: 'dropdown', options: WEBSITE_AVAILABLE_OPTIONS.filter(o => o.value) },
    ],
  },
  {
    id: 'ota_challenges', label: 'OTA & Challenges',
    filters: [
      { key: 'existing_ota_connected', label: 'Existing OTA Status', type: 'dropdown', options: EXISTING_OTA_OPTIONS.filter(o => o.value) },
      { key: 'existing_ota_info', label: 'Existing OTA Info', type: 'search', op: 'regex' },
      { key: 'key_challenges', label: 'Key Challenges', type: 'search', op: 'regex' },
      { key: 'no_of_rooms', label: 'No. of Rooms', type: 'range' },
      { key: 'no_of_properties', label: 'No. of Properties', type: 'range' },
    ],
  },
  {
    id: 'payment', label: 'Commercials',
    filters: [
      { key: 'payment_info.interested_plan', label: 'Interested Plan', type: 'dropdown', options: INTERESTED_PLAN_OPTIONS.filter(o => o.value) },
      { key: 'payment_info.setup_payment_status', label: 'Payment Status', type: 'dropdown', options: SETUP_PAYMENT_STATUS_OPTIONS.filter(o => o.value) },
      { key: 'payment_info.one_time_setup_fee', label: 'Setup Fee', type: 'range' },
      { key: 'payment_info.token_advance', label: 'Token Advance', type: 'range' },
      { key: 'payment_info.pending_payment', label: 'Pending Payment', type: 'range' },
      { key: 'payment_info.payment_completed_date', label: 'Payment Date', type: 'date_range' },
    ],
  },
  {
    id: 'property_details', label: 'Property Details',
    filters: [
      { key: 'property_details.company_name', label: 'Company Name', type: 'search', op: 'regex' },
      { key: 'property_details.gst_no', label: 'GST No.', type: 'search', op: 'regex' },
      { key: 'property_details.gst_no', label: 'Has GST', type: 'boolean', id: 'has_gst' },
      { key: 'property_details.live_since_years', label: 'Live Since (years)', type: 'range' },
      { key: 'property_details.observed_occupancy', label: 'Observed Occupancy %', type: 'range' },
      { key: 'property_details.expected_occupancy', label: 'Expected Occupancy %', type: 'range' },
    ],
  },
  {
    id: 'documents', label: 'Document Collection',
    filters: [
      { key: 'document_collection.google_map_link', label: 'Has Google Map Link', type: 'boolean' },
      { key: 'document_collection.document_drive_link_updated_time', label: 'Drive Link Updated', type: 'boolean' },
    ],
  },
  {
    id: 'pef', label: 'PEF Document',
    filters: [
      { key: 'documentCollection.pefPdfUrl', label: 'PEF PDF Uploaded', type: 'boolean' },
    ],
  },
  {
    id: 'checklist', label: 'Property Checklist',
    filters: [
      { key: 'property_checklist_info.master_form', label: 'Master Form', type: 'dropdown', options: CHECKLIST_STATUS_OPTIONS.filter(o => o.value) },
      { key: 'property_checklist_info.noc_agreement', label: 'NOC Agreement', type: 'dropdown', options: CHECKLIST_STATUS_OPTIONS.filter(o => o.value) },
      { key: 'property_checklist_info.lease_agreement', label: 'Lease Agreement', type: 'dropdown', options: CHECKLIST_STATUS_OPTIONS.filter(o => o.value) },
      { key: 'property_checklist_info.electricity_bill', label: 'Electricity Bill', type: 'dropdown', options: CHECKLIST_STATUS_OPTIONS.filter(o => o.value) },
      { key: 'property_checklist_info.pan_card', label: 'PAN Card', type: 'dropdown', options: CHECKLIST_STATUS_OPTIONS.filter(o => o.value) },
      { key: 'property_checklist_info.aadhar_card', label: 'Aadhar Card', type: 'dropdown', options: CHECKLIST_STATUS_OPTIONS.filter(o => o.value) },
      { key: 'property_checklist_info.gst_certificate', label: 'GST Certificate', type: 'dropdown', options: CHECKLIST_STATUS_OPTIONS.filter(o => o.value) },
      { key: 'property_checklist_info.cancelled_check_and_bank_details', label: 'Cancelled Cheque', type: 'dropdown', options: CHECKLIST_STATUS_OPTIONS.filter(o => o.value) },
      { key: 'property_checklist_info.property_photos', label: 'Property Photos', type: 'dropdown', options: CHECKLIST_STATUS_OPTIONS.filter(o => o.value) },
      { key: 'property_checklist_info.document_collection_status', label: 'Collection Status', type: 'dropdown', options: CHECKLIST_STATUS_OPTIONS.filter(o => o.value) },
    ],
  },
  {
    id: 'bank', label: 'Bank Details',
    filters: [
      { key: 'bankDetails.bankName', label: 'Bank Name', type: 'search', op: 'regex' },
      { key: 'bankDetails.accountNumber', label: 'Has Account No.', type: 'boolean' },
      { key: 'bankDetails.ifscCode', label: 'Has IFSC Code', type: 'boolean' },
      { key: 'bankDetails.cancelledChequePdfUrl', label: 'Cheque PDF Uploaded', type: 'boolean' },
    ],
  },
  {
    id: 'ota_info', label: 'OTA Information',
    filters: [
      { key: 'ota_info.ota_name', label: 'OTA Platform', type: 'dropdown', op: 'array_elem', options: OTA_NAME_OPTIONS.filter(o => o.value) },
      { key: 'ota_info.ota_status', label: 'OTA Status', type: 'dropdown', op: 'array_elem', options: OTA_STATUS_OPTIONS.filter(o => o.value) },
      { key: 'ota_info.locked', label: 'OTA Submitted & Locked', type: 'dropdown', op: 'array_elem_bool', options: [
        { value: 'true', label: 'Yes (Locked)' }, { value: 'false', label: 'No (Not Locked)' }
      ]},
      { key: 'ota_info.timer_running', label: 'Timer Running', type: 'dropdown', op: 'array_elem_bool', options: [
        { value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }
      ]},
    ],
  },
  {
    id: 'labels_section', label: 'Labels',
    filters: [
      { key: 'labels', label: 'Labels', type: 'labels_multi' },
    ],
  },
];

const AdvancedFilterPanel = ({ isOpen, onClose, onApply, activeFilters = {} }) => {
  const [activeTab, setActiveTab] = useState('lead_info');
  const [localFilters, setLocalFilters] = useState(activeFilters);
  const [availableLabels, setAvailableLabels] = useState([]);
  const panelRef = useRef(null);

  // Fetch labels from API
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_BASE}/api/labels`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setAvailableLabels(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  // Sync local state when parent activeFilters change (e.g. on restore from localStorage)
  useEffect(() => {
    setLocalFilters(activeFilters);
  }, [activeFilters]);

  const activeCount = useMemo(() => {
    return Object.keys(localFilters).filter(k => {
      const f = localFilters[k];
      if (!f) return false;
      if (f.value === '' || f.value === null || f.value === undefined) return false;
      if (Array.isArray(f.value) && f.value.every(v => !v && v !== 0)) return false;
      return true;
    }).length;
  }, [localFilters]);

  const setFilter = (filterKey, op, value) => {
    setLocalFilters(prev => ({ ...prev, [filterKey]: { op, value } }));
  };

  const removeFilter = (filterKey) => {
    setLocalFilters(prev => { const next = { ...prev }; delete next[filterKey]; return next; });
  };

  const clearAll = () => { setLocalFilters({}); onApply({}); };

  const handleApply = () => {
    const cleaned = {};
    for (const [key, val] of Object.entries(localFilters)) {
      if (!val || val.value === '' || val.value === null || val.value === undefined) continue;
      if (Array.isArray(val.value) && val.value.every(v => !v && v !== 0)) continue;
      cleaned[key] = val;
    }
    onApply(cleaned);
  };

  // Stop propagation on all clicks inside the panel to prevent double-click/toggle issues
  const handlePanelClick = (e) => { e.stopPropagation(); };

  const renderFilterInput = (filter) => {
    const filterId = filter.id || filter.key;
    const current = localFilters[filterId];

    switch (filter.type) {
      case 'search':
        return (
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" value={current?.value || ''}
              onChange={(e) => setFilter(filterId, filter.op || 'regex', e.target.value)}
              placeholder={`Search ${filter.label.toLowerCase()}...`}
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-teal-500 outline-none"
              data-testid={`filter-${filterId}`} />
          </div>
        );
      case 'dropdown':
        return (
          <select value={current?.value || (current?.op === 'array_contains' ? JSON.stringify(current.value) : '')}
            onChange={(e) => {
              const val = e.target.value;
              if (!val) { removeFilter(filterId); return; }
              if (filter.op === 'array_elem') {
                setFilter(filterId, 'array_contains', { [filter.key.split('.').pop()]: val });
              } else if (filter.op === 'array_elem_bool') {
                setFilter(filterId, 'array_contains', { [filter.key.split('.').pop()]: val === 'true' });
              } else {
                setFilter(filterId, 'eq', val);
              }
            }}
            className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-teal-500 outline-none bg-white"
            data-testid={`filter-${filterId}`}>
            <option value="">All</option>
            {filter.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        );
      case 'boolean':
        return (
          <select value={current ? (current.op === 'exists' ? 'yes' : current.op === 'not_exists' ? 'no' : '') : ''}
            onChange={(e) => {
              const val = e.target.value;
              if (!val) { removeFilter(filterId); return; }
              setFilter(filterId, val === 'yes' ? 'exists' : 'not_exists', true);
            }}
            className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-teal-500 outline-none bg-white"
            data-testid={`filter-${filterId}`}>
            <option value="">All</option>
            <option value="yes">Yes (Present)</option>
            <option value="no">No (Missing)</option>
          </select>
        );
      case 'range':
        return (
          <div className="flex items-center gap-2">
            <input type="number" value={current?.value?.[0] ?? ''}
              onChange={(e) => {
                const low = e.target.value; const high = current?.value?.[1] ?? '';
                if (!low && !high) { removeFilter(filterId); return; }
                setFilter(filterId, 'between', [low || null, high || null]);
              }}
              placeholder="Min" className="w-1/2 px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-teal-500 outline-none"
              data-testid={`filter-${filterId}-min`} />
            <span className="text-gray-400 text-xs">to</span>
            <input type="number" value={current?.value?.[1] ?? ''}
              onChange={(e) => {
                const high = e.target.value; const low = current?.value?.[0] ?? '';
                if (!low && !high) { removeFilter(filterId); return; }
                setFilter(filterId, 'between', [low || null, high || null]);
              }}
              placeholder="Max" className="w-1/2 px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-teal-500 outline-none"
              data-testid={`filter-${filterId}-max`} />
          </div>
        );
      case 'date_range':
        return (
          <div className="flex items-center gap-2">
            <input type="date" value={current?.value?.[0] || ''}
              onChange={(e) => {
                const from = e.target.value; const to = current?.value?.[1] || '';
                if (!from && !to) { removeFilter(filterId); return; }
                setFilter(filterId, 'date_between', [from || null, to || null]);
              }}
              className="w-1/2 px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-teal-500 outline-none"
              data-testid={`filter-${filterId}-from`} />
            <span className="text-gray-400 text-xs">to</span>
            <input type="date" value={current?.value?.[1] || ''}
              onChange={(e) => {
                const to = e.target.value; const from = current?.value?.[0] || '';
                if (!from && !to) { removeFilter(filterId); return; }
                setFilter(filterId, 'date_between', [from || null, to || null]);
              }}
              className="w-1/2 px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-teal-500 outline-none"
              data-testid={`filter-${filterId}-to`} />
          </div>
        );
      case 'labels_multi': {
        const selectedIds = (current?.op === 'in' && Array.isArray(current?.value)) ? current.value : [];
        const toggleLabel = (labelId) => {
          const next = selectedIds.includes(labelId) ? selectedIds.filter(id => id !== labelId) : [...selectedIds, labelId];
          if (next.length === 0) { removeFilter(filterId); } else { setFilter(filterId, 'in', next); }
        };
        return (
          <div className="col-span-2 lg:col-span-3 xl:col-span-4 space-y-2" data-testid="filter-labels-multi">
            {availableLabels.length === 0 ? (
              <p className="text-xs text-gray-400">No labels available</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {availableLabels.map(lbl => {
                  const active = selectedIds.includes(lbl.id);
                  return (
                    <button key={lbl.id} type="button" onClick={() => toggleLabel(lbl.id)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        active ? 'border-teal-400 bg-teal-50 text-teal-800 ring-1 ring-teal-200' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                      data-testid={`filter-label-${lbl.id}`}>
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: lbl.color || '#9ca3af' }} />
                      {lbl.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      }
      default:
        return null;
    }
  };

  const getFilterLabel = (filterKey) => {
    for (const section of FILTER_SECTIONS) {
      const f = section.filters.find(fl => (fl.id || fl.key) === filterKey);
      if (f) return f.label;
    }
    return filterKey;
  };

  const getFilterValueLabel = (filterKey, filter) => {
    if (!filter) return '';
    if (filter.op === 'exists') return 'Present';
    if (filter.op === 'not_exists') return 'Missing';
    if (filter.op === 'between' && Array.isArray(filter.value)) return `${filter.value[0] || '...'} - ${filter.value[1] || '...'}`;
    if (filter.op === 'date_between' && Array.isArray(filter.value)) return `${filter.value[0] || '...'} ~ ${filter.value[1] || '...'}`;
    if (filter.op === 'array_contains' && typeof filter.value === 'object') return Object.values(filter.value).join(', ');
    if (filter.op === 'in' && Array.isArray(filter.value) && filterKey === 'labels') {
      return filter.value.map(id => availableLabels.find(l => l.id === id)?.name || id).join(', ');
    }
    if (filter.op === 'regex') return `"${filter.value}"`;
    return String(filter.value);
  };

  if (!isOpen) return null;

  return (
    <div ref={panelRef} onClick={handlePanelClick}
      className="bg-white rounded-xl border border-gray-200 shadow-md sticky top-0 z-20 flex flex-col"
      style={{ maxHeight: '360px' }}
      data-testid="advanced-filter-panel">

      {/* Header - fixed */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-teal-600" />
          <span className="font-semibold text-sm text-gray-800">Advanced Filters</span>
          {activeCount > 0 && (
            <span className="bg-teal-100 text-teal-700 text-xs font-bold px-2 py-0.5 rounded-full">{activeCount}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {activeCount > 0 && (
            <button onClick={clearAll} className="text-xs text-gray-500 hover:text-red-500 flex items-center gap-1" data-testid="filter-clear-all">
              <RotateCcw size={12} /> Clear All
            </button>
          )}
          <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded" data-testid="filter-close">
            <X size={16} className="text-gray-500" />
          </button>
        </div>
      </div>

      {/* Active filter tags - fixed */}
      {activeCount > 0 && (
        <div className="flex flex-wrap gap-1.5 px-4 py-2 bg-teal-50/50 border-b border-gray-100 flex-shrink-0">
          {Object.entries(localFilters).map(([key, val]) => {
            if (!val || val.value === '' || val.value === null) return null;
            return (
              <span key={key} className="inline-flex items-center gap-1 bg-white border border-teal-200 text-teal-700 text-xs px-2 py-1 rounded-full" data-testid={`filter-tag-${key}`}>
                <span className="font-medium">{getFilterLabel(key)}:</span>
                <span className="text-teal-600 max-w-[120px] truncate">{getFilterValueLabel(key, val)}</span>
                <button onClick={() => removeFilter(key)} className="hover:text-red-500 ml-0.5"><X size={12} /></button>
              </span>
            );
          })}
        </div>
      )}

      {/* Tabs - fixed */}
      <div className="flex overflow-x-auto border-b border-gray-200 bg-gray-50/50 flex-shrink-0" style={{ scrollbarWidth: 'none' }}>
        {FILTER_SECTIONS.map(section => {
          const sectionActiveCount = section.filters.filter(f => {
            const fId = f.id || f.key;
            const val = localFilters[fId];
            return val && val.value !== '' && val.value !== null;
          }).length;
          return (
            <button key={section.id} onClick={() => setActiveTab(section.id)}
              className={`px-3 py-2 text-xs font-medium whitespace-nowrap border-b-2 transition-colors flex-shrink-0 ${
                activeTab === section.id ? 'border-teal-500 text-teal-700 bg-white' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid={`filter-tab-${section.id}`}>
              {section.label}
              {sectionActiveCount > 0 && (
                <span className="ml-1 bg-teal-500 text-white text-[10px] font-bold w-4 h-4 inline-flex items-center justify-center rounded-full">{sectionActiveCount}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Filter fields - scrollable area */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {FILTER_SECTIONS.map(section => {
          if (activeTab !== section.id) return null;
          return (
            <div key={section.id} className="p-4 grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid={`filter-section-${section.id}`}>
              {section.filters.map(filter => {
                const filterId = filter.id || filter.key;
                return (
                  <div key={filterId} className="space-y-1">
                    <label className="text-xs font-medium text-gray-600">{filter.label}</label>
                    {renderFilterInput(filter)}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Footer - fixed */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-t border-gray-200 flex-shrink-0">
        <span className="text-xs text-gray-400">
          {activeCount > 0 ? `${activeCount} filter${activeCount > 1 ? 's' : ''} active` : 'No filters applied'}
        </span>
        <div className="flex gap-2">
          <button onClick={() => { setLocalFilters({}); }} className="px-3 py-1.5 text-sm border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors" data-testid="filter-clear-btn">
            Clear
          </button>
          <button onClick={handleApply} className="px-4 py-1.5 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium" data-testid="filter-apply-btn">
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdvancedFilterPanel;
